/* ==========================================================================
   ZWANZE — zwanze.js
   Registre minimal et utilitaires partagés. Aucun framework, aucune
   dépendance. Chaque comportement est un custom element autonome, défini
   dans son propre fichier et chargé uniquement là où il sert.
   Budget : < 4 Ko non compressé.
   ========================================================================== */
(function () {
  'use strict';

  const Z = (window.Zwanze = window.Zwanze || {});

  /* --- Formatage monétaire -------------------------------------------------
     Reproduit le format défini par le marchand dans Shopify, sans dépendance. */
  Z.formatMoney = function (cents) {
    const value = (cents / 100).toFixed(2);
    const format = Z.moneyFormat || '{{amount_with_comma_separator}}';
    const [intPart, decPart] = value.split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (format.indexOf('comma_separator') !== -1) {
      return format.replace(/\{\{\s*\w+\s*\}\}/, grouped + ',' + decPart);
    }
    if (format.indexOf('no_decimals') !== -1) {
      return format.replace(/\{\{\s*\w+\s*\}\}/, grouped);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, grouped + '.' + decPart);
  };

  /* --- Annonce aux lecteurs d'écran ---------------------------------------
     Toute information transmise par une animation existe aussi en texte. */
  Z.announce = function (message) {
    const region = document.getElementById('z-live');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  };

  /* --- Événements applicatifs ---------------------------------------------- */
  Z.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent('zwanze:' + name, { detail: detail, bubbles: true }));
  };
  Z.on = function (name, handler) {
    document.addEventListener('zwanze:' + name, handler);
  };

  /* --- Requêtes panier ------------------------------------------------------
     Un seul point d'entrée : toute mutation du panier passe par ici et
     émet un événement, ce qui garde header, tiroir et page panier alignés. */
  Z.cart = {
    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || Z.strings.error);
      return data;
    },
    async add(items) {
      const data = await this.request(Z.routes.cartAdd, { items: items });
      const cart = await this.get();
      Z.emit('cart:updated', { cart: cart, added: data });
      return cart;
    },
    async change(payload) {
      const cart = await this.request(Z.routes.cartChange, payload);
      Z.emit('cart:updated', { cart: cart });
      return cart;
    },
    async get() {
      const response = await fetch(Z.routes.cart + '.js', { headers: { Accept: 'application/json' } });
      return response.json();
    }
  };

  /* --- Section Rendering API ------------------------------------------------
     Permet de rafraîchir une portion de page (filtres, grille, panier)
     sans rechargement complet et sans dupliquer le balisage en JavaScript. */
  Z.fetchSection = async function (url, sectionId) {
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    const response = await fetch(url + separator + 'section_id=' + sectionId);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  };

  /* --- Piège de focus -------------------------------------------------------
     Utilisé par tout panneau modal : tiroir panier, menu, filtres. */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),summary';

  Z.trapFocus = function (container, firstElement) {
    const nodes = Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return function () {};

    const first = firstElement || nodes[0];
    const last = nodes[nodes.length - 1];
    const previous = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === nodes[0]) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        nodes[0].focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  };

  /* --- Verrouillage du défilement de fond ----------------------------------- */
  let lockCount = 0;
  Z.lockScroll = function (locked) {
    lockCount = Math.max(0, lockCount + (locked ? 1 : -1));
    const shouldLock = lockCount > 0;
    document.documentElement.style.overflow = shouldLock ? 'hidden' : '';
    if (shouldLock) {
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.paddingRight = gap > 0 ? gap + 'px' : '';
    } else {
      document.documentElement.style.paddingRight = '';
    }
  };

  /* --- Classe de base des panneaux -------------------------------------------
     Tiroir panier, menu mobile et panneau de filtres en héritent : ils
     partagent ouverture, fermeture, Échap, focus et verrouillage du scroll. */
  Z.Panel = class extends HTMLElement {
    connectedCallback() {
      this._release = null;
      this.addEventListener('click', this._onBackdrop.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));
      this.querySelectorAll('[data-panel-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
    }
    open() {
      this.setAttribute('open', '');
      Z.lockScroll(true);
      const panel = this.querySelector('[data-panel]') || this;
      this._release = Z.trapFocus(panel, this.querySelector('[data-panel-focus]'));
      Z.emit('panel:open', { panel: this });
    }
    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      Z.lockScroll(false);
      if (this._release) { this._release(); this._release = null; }
      Z.emit('panel:close', { panel: this });
    }
    _onKeydown(event) {
      if (event.key === 'Escape') { event.stopPropagation(); this.close(); }
    }
    _onBackdrop(event) {
      if (event.target.hasAttribute('data-panel-backdrop')) this.close();
    }
  };

  /* --- Compteur du panier dans l'en-tête --------------------------------------
     Se met à jour à chaque mutation, sans rechargement. */
  Z.on('cart:updated', function (event) {
    const count = event.detail.cart ? event.detail.cart.item_count : 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.toggleAttribute('hidden', count === 0);
    });
    /* Correction (Claude) : le titre du tiroir panier ("Panier (0)") utilise un
       attribut différent (data-cart-count-text) et restait bloqué à la valeur
       affichée au chargement de la page, jamais mise à jour après un ajout au
       panier — seul le badge de l'en-tête (data-cart-count) l'était. */
    document.querySelectorAll('[data-cart-count-text]').forEach(function (node) {
      node.textContent = '(' + count + ')';
    });
  });

  /* --- En-tête : réduction au défilement ---------------------------------------
     IntersectionObserver plutôt qu'un écouteur de scroll : coût processeur nul. */
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;pointer-events:none';
  document.addEventListener('DOMContentLoaded', function () {
    document.body.prepend(sentinel);
    new IntersectionObserver(function (entries) {
      document.documentElement.classList.toggle('z-scrolled', !entries[0].isIntersecting);
    }).observe(sentinel);
  });
})();
